/** @jsxImportSource @emotion/react */

import React, { ReactNode } from 'react';
import { CSSObject } from '@emotion/react';
import { CSSProp } from '../../../types/emotion';

export type TextStyles = 'paragraph' | 'note' | 'l' | 'm' | 'sm' | 's';

export interface TextProps {
  /**
   * Text style<br />
   * `paragraph`: Text M Regular (Bosch Styleguide)<br />
   * `note`: Text S Regular (Bosch Styleguide)
   */
  style?: TextStyles;
  /**
   * Text content
   */
  children?: ReactNode;
}

/**
 * The `Text` component displays a text with a specific style.
 */
export function Text({
  style,
  children,
  ...props
}: TextProps & Omit<Partial<React.HTMLAttributes<HTMLParagraphElement>>, keyof TextProps> & CSSProp) {
  return (
    <p
      css={
        {
          fontFamily: 'Bosch Sans, sans-serif',
          fontWeight: 'normal',
          whiteSpace: 'pre-line',
          margin: 0,
          padding: 0,
          fontSize:
            (style &&
              {
                paragraph: '1rem',
                note: '0.75rem',
                l: '1.25rem',
                m: '1rem',
                sm: '0.875rem',
                s: '0.75rem',
              }[style]) ??
            '1rem',
          lineHeight:
            (style &&
              {
                paragraph: '1.5rem',
                note: '1.125rem',
                l: '1.875rem',
                m: '1.5rem',
                sm: '1.375rem',
                s: '1.125rem',
              }[style]) ??
            '1.5rem',
          li: { listStylePosition: 'inside' },
        } as CSSObject
      }
      {...props}
    >
      {children}
    </p>
  );
}
